import { useState } from "react";
import { motion } from "framer-motion";
import { Screen } from "../components/Screen";
import { Ref } from "../components/Ref";
import { Reveal } from "../components/Reveal";
import { Segmented } from "../components/Segmented";
import { Placeholder } from "../components/Placeholder";
import { CountUp } from "../components/CountUp";
import { useSnak } from "../snak/SnakContext";
type Key = "skin" | "blood" | "nodes" | "viscera";

// MAVORIC, mogamulizumab arm — compartment response rates (Kim et al. 2018). Verify against primary publication.
const COMPARTMENTS: {
  key: Key;
  label: string;
  short: string;
  value: number;
  assessment: string;
}[] = [{
  key: "skin",
  label: "Skin",
  short: "Skin",
  value: 42,
  assessment: "Assessed using the modified Severity Weighted Assessment Tool (mSWAT)."
}, {
  key: "blood",
  label: "Blood",
  short: "Blood",
  value: 68,
  assessment: "Assessed by flow cytometry of circulating Sézary cells in patients with blood involvement."
}, {
  key: "nodes",
  label: "Lymph nodes",
  short: "Nodes",
  value: 17,
  assessment: "Assessed by imaging in patients with lymph-node involvement."
}, {
  key: "viscera",
  label: "Viscera",
  short: "Viscera",
  value: 0,
  assessment: "Few patients had visceral involvement; no visceral responses were reported."
}];
export function P5CloserLook() {
  const {
    explore
  } = useSnak();
  const [active, setActive] = useState<Key>("blood");
  const current = COMPARTMENTS.find(c => c.key === active)!;
  const select = (k: Key) => {
    setActive(k);
    explore();
  };
  return <Screen overline={<>
          MAVORIC · randomised phase 3 trial<Ref n={2} data-id="element-498" data-mp="1tubgce4g8mum" />
        </>} refTarget={2} footnote={<p data-id="element-499" data-mp="1ysd0l2dm2s7o">
          Proportion of patients with compartment involvement at baseline who achieved a response in that compartment,
          mogamulizumab arm of MAVORIC (n=186).<Ref n={2} data-id="element-500" data-mp="4e2cwx8r643h" /> Evaluable n per compartment:{" "}
          <Placeholder data-id="element-501" data-mp="4els4670qedt">insert from source</Placeholder>. Comparator-arm (vorinostat) results are reported in the
          publication. Values to be verified against the primary publication. Different study population and design from
          MINT; not directly comparable.
        </p>} data-id="element-497" data-mp="1os41ngs8isol">
      <Reveal delay={0.1} data-id="element-502" data-mp="6d52aqlh934t">
        <h1 className="mt-5 font-display text-[32px] font-medium leading-[1.1] tracking-[-0.01em]" data-id="element-503" data-mp="697f9tvs3rfn">
          Why blood involvement deserves attention.
        </h1>
      </Reveal>

      <Reveal delay={0.25} data-id="element-504" data-mp="fafdkbyaos9q">
        <div className="mt-6" data-id="element-505" data-mp="ban1hp9p03zm">
          <Segmented options={COMPARTMENTS.map(c => ({
          value: c.key,
          label: c.short
        }))} value={active} onChange={v => select(v as Key)} data-id="element-506" data-mp="bbg9etke0zjc" />
        </div>

        <div className="mt-6 flex items-start" data-id="element-507" data-mp="p3w7rh2cg9dx">
          <span className="font-display text-[112px] font-medium leading-[0.82] tracking-[-0.03em]" data-id="element-508" data-mp="k401fb66c5v7">
            <span key={active} data-id="element-509" data-mp="z1am9b4pio3l">
              <CountUp to={current.value} decimals={0} duration={1.2} data-id="element-510" data-mp="yy4x5vw2vm7v" />
            </span>
          </span>
          <span className="ml-1 font-display text-[42px] font-medium leading-none" data-id="element-511" data-mp="tz1taeru3m1h">%</span>
        </div>
        <p className="overline mt-4 leading-[1.6]" data-id="element-512" data-mp="1dl3n1mfae8kh">
          {current.label} response, mogamulizumab arm<Ref n={2} data-id="element-513" data-mp="1hmfbvpqc6prm" />
        </p>
        <p className="mt-2 text-[13px] leading-relaxed text-[var(--s-muted)]" data-id="element-514" data-mp="13pa2bfrez8rm">{current.assessment}</p>

        <div className="mt-6 space-y-2.5" data-id="element-515" data-mp="18nbsekn9c8cl">
          {COMPARTMENTS.map((c, i) => {
          const on = c.key === active;
          return <button key={c.key} onClick={() => select(c.key)} className={`grid w-full grid-cols-[5.5rem_1fr_2.5rem] items-center gap-3 text-left transition-opacity duration-200 ${on ? "opacity-100" : "opacity-60 hover:opacity-90"}`} data-id="element-516" data-mp="txnpxg3cb3na" data-mpi={i}>
                <span className={`text-[13px] ${on ? "font-semibold" : ""}`} data-id="element-517" data-mp="b0cxaec7r8gt">{c.label}</span>
                <span className="relative h-[6px] bg-[var(--s-track)]" data-id="element-518" data-mp="fywigcizgg84">
                  <motion.span className="absolute inset-y-0 left-0 bg-[var(--s-fg)]" initial={{
                width: "0%"
              }} animate={{
                width: `${c.value}%`
              }} transition={{
                duration: 1.4,
                delay: 0.5 + i * 0.1,
                ease: [0.16, 1, 0.3, 1]
              }} data-id="element-519" data-mp="g1aa0begfaax" />
                </span>
                <span className="tabular text-right text-[13px]" data-id="element-520" data-mp="14lz26ymiyjsd">{c.value}%</span>
              </button>;
        })}
        </div>
      </Reveal>

      <Reveal delay={0.55} data-id="element-521" data-mp="zjritnzd81g0">
        <div className="mt-8 border-l-2 border-[var(--s-fg)] pl-4" data-id="element-522" data-mp="vjzj80ry95ms">
          <p className="overline text-[10px]" data-id="element-523" data-mp="vmvhjq7plwbr">
            Guidance · German expert recommendations<Ref n={3} data-id="element-524" data-mp="qlo6nia9sh0v" />
          </p>
          <p className="mt-2 font-display text-[19px] leading-snug" data-id="element-525" data-mp="lml5wa1bowyp">
            Expert recommendations highlight the relevance of assessing blood involvement in patients with MF or SS being
            considered for systemic therapy.
          </p>
          <p className="mt-2 text-[12px]" data-id="element-526" data-mp="2g0vvikpqrv5j">
            <Placeholder data-id="element-527" data-mp="2nk5080naz5o9">insert verified key recommendation(s) verbatim with page reference</Placeholder>
          </p>
        </div>
      </Reveal>
    </Screen>;
}